import HostTarget from '../types/host/HostTarget';
import Agent from '../types/instance/Agent';
import { HostFormData, HostFormValidation } from '../types/host/HostFormState';

export class HostFormValidator {

    static validateName(
            name: string,
            currentId: number,
            hosts: HostTarget[]
    ): { isValid: boolean; isUsed: boolean; className: string } {
        const trimmedName = name.trim();

        if (trimmedName === '') {
            return {
                isValid: false,
                isUsed: false,
                className: 'is-invalid'
            };
        }

        // Check for duplicate names
        const duplicateIndex = hosts.findIndex(
                host => host.id !== currentId &&
                        host.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (duplicateIndex !== -1) {
            return {
                isValid: false,
                isUsed: true,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            isUsed: false,
            className: 'is-valid'
        };
    }

    static validateHostAddress(
            hostAddress: string
    ): { isValid: boolean; isUsed: boolean; className: string } {
        const trimmedAddress = hostAddress.trim();

        if (trimmedAddress === '') {
            return {
                isValid: false,
                isUsed: false,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            isUsed: false,
            className: 'is-valid'
        };
    }

    static validatePort(port: number): { isValid: boolean; className: string } {
        const portString = port.toString();
        const regexp = /^[0-9\b]+$/;

        if ((portString.length > 0 || regexp.test(portString)) &&
                (portString.length < 6) &&
                (port <= 65535)) {
            return {
                isValid: true,
                className: 'is-valid'
            };
        }

        return {
            isValid: false,
            className: 'is-invalid'
        };
    }

    static validateType(type: string): { isValid: boolean; className: string } {
        if (type.trim() === '') {
            return {
                isValid: false,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            className: 'is-valid'
        };
    }

    static validateAgentId(agentId: number): { isValid: boolean; className: string } {
        if (agentId === 0) {
            return {
                isValid: false,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            className: 'is-valid'
        };
    }

    static validateUsername(username: string, hostType: string): { className: string } {
        if (hostType !== 'windows' && username.trim() === '') {
            return { className: 'is-invalid' };
        }

        if (hostType !== 'windows' && username.trim() !== '') {
            return { className: 'is-valid' };
        }

        return { className: '' };
    }

    static validatePasswordAndKeyfile(
            password: string,
            keyfile: string,
            hostType: string
    ): {
        password: { className: string };
        keyfile: { className: string };
        isValid: boolean;
    } {
        if (hostType === 'windows') {
            return {
                password: { className: '' },
                keyfile: { className: '' },
                isValid: true
            };
        }

        const trimmedPassword = password.trim();
        const trimmedKeyfile = keyfile.trim();

        // Both filled - invalid
        if (trimmedPassword !== '' && trimmedKeyfile !== '') {
            return {
                password: { className: 'is-invalid' },
                keyfile: { className: 'is-invalid' },
                isValid: false
            };
        }

        // Both empty - invalid
        if (trimmedPassword === '' && trimmedKeyfile === '') {
            return {
                password: { className: 'is-invalid' },
                keyfile: { className: 'is-invalid' },
                isValid: false
            };
        }

        // Only keyfile filled - valid
        if (trimmedPassword === '' && trimmedKeyfile !== '') {
            return {
                password: { className: '' },
                keyfile: { className: 'is-valid' },
                isValid: true
            };
        }

        // Only password filled - valid
        if (trimmedPassword !== '' && trimmedKeyfile === '') {
            return {
                password: { className: 'is-valid' },
                keyfile: { className: '' },
                isValid: true
            };
        }

        return {
            password: { className: '' },
            keyfile: { className: '' },
            isValid: true
        };
    }

    static validateRegion(region: string, hostType: string): { isValid: boolean; className: string } {
        if (hostType === 'awsrds' && region.trim() === '') {
            return {
                isValid: false,
                className: 'is-invalid'
            };
        }

        if (hostType === 'awsrds' && region.trim() !== '') {
            return {
                isValid: true,
                className: 'is-valid'
            };
        }

        return {
            isValid: true,
            className: ''
        };
    }

    static validateCompleteForm(
            formData: HostFormData,
            hosts: HostTarget[]
    ): {
        isValid: boolean;
        validation: HostFormValidation;
    } {
        let isValid = true;

        // Validate name
        const nameValidation = this.validateName(formData.name, formData.id, hosts);
        if (!nameValidation.isValid) isValid = false;

        // Validate host address
        const hostAddressValidation = this.validateHostAddress(formData.hostAddress);
        if (!hostAddressValidation.isValid) isValid = false;

        // Validate type
        const typeValidation = this.validateType(formData.type);
        if (!typeValidation.isValid) isValid = false;

        // Validate agent
        const agentValidation = this.validateAgentId(formData.agentId);
        if (!agentValidation.isValid) isValid = false;

        // Validate port
        const portValidation = this.validatePort(formData.port);
        if (!portValidation.isValid) isValid = false;

        // Validate username (non-Windows only)
        const usernameValidation = this.validateUsername(formData.username, formData.type);

        // Validate password and keyfile
        const passwordKeyfileValidation = this.validatePasswordAndKeyfile(
                formData.password,
                formData.keyfile,
                formData.type
        );
        if (!passwordKeyfileValidation.isValid) isValid = false;

        // Validate region (AWS RDS only)
        const regionValidation = this.validateRegion(formData.region, formData.type);
        if (!regionValidation.isValid) isValid = false;

        const validation: HostFormValidation = {
            name: nameValidation,
            hostAddress: hostAddressValidation,
            type: typeValidation,
            agentId: agentValidation,
            port: portValidation,
            username: usernameValidation,
            password: passwordKeyfileValidation.password,
            keyfile: passwordKeyfileValidation.keyfile,
            region: regionValidation,
            isFormValid: isValid
        };

        return {
            isValid,
            validation
        };
    }
}

// Utility functions for business logic
export class HostFormUtils {

    static canViewPassword(isCreate: boolean, isUpdate: boolean, passwordChanged: boolean): boolean {
        return isCreate || (isUpdate && passwordChanged);
    }

    static sanitizeNumericInput(value: string): string {
        return value.replace(/\D/g, ""); // Remove non-digits
    }

    // A Linux agent monitors Windows with wmic (WMI over DCOM), so WinRM
    // needs a Windows agent. An agent that has never registered has no os.
    static agentSupportsWinRM(agent?: Agent): boolean {
        return !agent?.os?.startsWith('Linux');
    }

    static monitoringMethodToSave(type: string, method: string | null, agent?: Agent): string | null {
        if (type !== 'windows') {
            return null;
        }

        return method === 'WinRM' && !HostFormUtils.agentSupportsWinRM(agent) ? 'WMI' : method;
    }
}

